import { flow } from 'lodash-es';
import { Anchor } from 'site-modules/personalized-search/components/anchor/anchor';
import { AnchorItem } from 'site-modules/personalized-search/components/anchor/anchor-item';
import { HeaderContent } from 'site-modules/personalized-search/components/header-content/header-content';
import { Overview } from 'site-modules/personalized-search/components/overview/overview';
import { OverviewItem } from 'site-modules/personalized-search/components/overview/overview-item';
import { Highlights } from 'site-modules/personalized-search/components/higlights/highlights';
import { OverviewCta } from 'site-modules/personalized-search/components/overview-cta/overview-cta';
import { Inventory } from 'site-modules/personalized-search/components/inventory/inventory';
import { Vin } from 'site-modules/personalized-search/components/vin/vin';
import { dataProviders } from 'site-modules/personalized-search/data-providers/data-providers';
import { withModelFiltersUpdate } from 'site-modules/personalized-search/utils/with-model-filters-update';
import { withLocationUpdate } from 'site-modules/personalized-search/utils/with-location-update';
import { Opinion } from 'site-modules/personalized-search/components/opinion/opinion';
import { Dealership } from 'site-modules/personalized-search/components/dealership/dealership';
import { DealershipItem } from 'site-modules/personalized-search/components/dealership-item/dealership-item';
import { Links } from 'site-modules/personalized-search/components/links/links';
import { COMPONENTS } from 'site-modules/personalized-search/constants/components';
import { LearnMore } from 'site-modules/personalized-search/components/learn-more/learn-more';
import {
  ANCHOR_COMPONENT,
  HEADER_COMPONENT,
  HIGHLIGHTS_COMPONENT,
  INVENTORY_COMPONENT,
  LEAD_CTA_COMPONENT,
  LEARN_MORE_COMPONENT_CREATIVE_ID,
  LINKS_COMPONENT_CREATIVE_ID,
  OPINION_COMPONENT,
  VERDICT_COMPONENT_CREATIVE_ID,
} from 'site-modules/personalized-search/constants/tracking';
import { Verdict } from 'site-modules/personalized-search/components/verdict/verdict';
import { LinksItem } from 'site-modules/personalized-search/components/links/links-item';
import { withComponentLoader } from 'site-modules/personalized-search/components/with-component-loader/with-component-loader';
import { OverviewSkeleton } from 'site-modules/personalized-search/components/overview/overview-skeleton/overview-skeleton';
import { OverviewItemSkeleton } from 'site-modules/personalized-search/components/overview/overview-item-skeleton/overview-item-skeleton';
import { HeaderSkeleton } from 'site-modules/personalized-search/components/header-content/header-skeleton/header-skeleton';
import { HighlightsSkeleton } from 'site-modules/personalized-search/components/higlights/highlights-skeleton/highlights-skeleton';
import { VerdictSkeleton } from 'site-modules/personalized-search/components/verdict/verdict-skeleton/verdict-skeleton';
import { StubComponent } from 'site-modules/personalized-search/components/stub-component/stub-component';
import { LearnMoreSkeleton } from 'site-modules/personalized-search/components/learn-more/learn-more-skeleton/learn-more-skeleton';
import { LinksSkeleton } from 'site-modules/personalized-search/components/links/links-skeleton/links-skeleton';
import { OverviewCtaSkeleton } from 'site-modules/personalized-search/components/overview-cta/overview-cta-skeleton/overview-cta-skeleton';
import { InventorySkeleton } from 'site-modules/personalized-search/components/inventory/inventory-skeleton/inventory-skeleton';
import { withHeaderContentDataAdapter } from 'site-modules/personalized-search/components/header-content/header-content-data-adapter';
import { withHighlightsDataAdapter } from 'site-modules/personalized-search/components/higlights/highlights-data-adapter';
import { withInventoryDataAdapter } from 'site-modules/personalized-search/components/inventory/inventory-data-adapter';
import { withOverviewCtaDataAdapter } from 'site-modules/personalized-search/components/overview-cta/overview-cta-data-adapter';
import { withOpinionDataAdapter } from 'site-modules/personalized-search/components/opinion/opinion-data-adapter';
import { OpinionSkeleton } from 'site-modules/personalized-search/components/opinion/opinion-skeleton/opinion-skeleton';
import { checkLoadedData } from 'site-modules/personalized-search/utils/check-loaded-data';
import { SUPPORTED_COMPONENTS } from 'site-modules/personalized-search/constants/supported-components';
import { DealershipItemSkeleton } from 'site-modules/personalized-search/components/dealership-item/dealership-item-skeleton/dealership-item-skeleton';
import { withInitialInventoryParamsUrlUpdate } from 'site-modules/personalized-search/utils/with-initial-inventory-params-url-update';
import { withInitialFilters } from 'site-modules/personalized-search/utils/with-initial-filters';
import { AnchorSkeleton } from 'site-modules/personalized-search/components/anchor/anchor-skeleton/anchor-skeleton';
import { Answer } from 'site-modules/personalized-search/components/answer/answer';
import { Extract } from 'site-modules/personalized-search/components/extract/extract';
import { ExtractSkeleton } from 'site-modules/personalized-search/components/extract/extract-skeleton/extract-skeleton';
import { AnswerSkeleton } from 'site-modules/personalized-search/components/answer/answer-skeleton/answer-skeleton';

/**
 * All component that use API should be wrapped into withComponentLoader hoc
 */
export const SEARCH_COMPONENTS_MAP = {
  [COMPONENTS.ANCHOR]: {
    name: COMPONENTS.ANCHOR,
    component: withComponentLoader({ skeleton: AnchorSkeleton })(Anchor),
  },
  [COMPONENTS.ANCHOR_ITEM]: {
    name: COMPONENTS.ANCHOR_ITEM,
    component: flow(
      withHeaderContentDataAdapter,
      withComponentLoader({ skeleton: HeaderSkeleton, checkDataLoaded: checkLoadedData(SUPPORTED_COMPONENTS.header) }),
      dataProviders.headerContentDataProvider,
      dataProviders.anchorItemDataProvider
    )(AnchorItem),
    creativeId: ANCHOR_COMPONENT,
  },
  [COMPONENTS.OVERVIEW]: {
    name: COMPONENTS.OVERVIEW,
    component: flow(withComponentLoader({ skeleton: OverviewSkeleton }))(Overview),
  },
  [COMPONENTS.OVERVIEW_ITEM]: {
    name: COMPONENTS.OVERVIEW_ITEM,
    component: flow([
      withComponentLoader({
        skeleton: OverviewItemSkeleton,
        checkDataLoaded: checkLoadedData(SUPPORTED_COMPONENTS.overviewItem),
      }),
      dataProviders.overviewItemDataProvider,
    ])(OverviewItem),
  },
  [COMPONENTS.OVERVIEW_ITEM_HEADER]: {
    name: COMPONENTS.OVERVIEW_ITEM_HEADER,
    component: flow([
      withHeaderContentDataAdapter,
      withComponentLoader({ skeleton: HeaderSkeleton, checkDataLoaded: checkLoadedData(SUPPORTED_COMPONENTS.header) }),
      dataProviders.headerContentDataProvider,
    ])(HeaderContent),
    creativeId: HEADER_COMPONENT,
  },
  [COMPONENTS.OVERVIEW_ITEM_HIGHLIGHTS]: {
    name: COMPONENTS.OVERVIEW_ITEM_HIGHLIGHTS,
    component: flow([
      withHighlightsDataAdapter,
      withComponentLoader({
        skeleton: HighlightsSkeleton,
        checkDataLoaded: checkLoadedData(SUPPORTED_COMPONENTS.highlights),
      }),
      dataProviders.highlightsDataProvider,
    ])(Highlights),
    creativeId: HIGHLIGHTS_COMPONENT,
  },
  [COMPONENTS.OVERVIEW_ITEM_OPINION]: {
    name: COMPONENTS.OVERVIEW_ITEM_OPINION,
    component: flow([
      withOpinionDataAdapter,
      withComponentLoader({
        skeleton: OpinionSkeleton,
        checkDataLoaded: checkLoadedData(SUPPORTED_COMPONENTS.opinion),
      }),
      dataProviders.opinionDataProvider,
    ])(Opinion),
    creativeId: OPINION_COMPONENT,
  },
  [COMPONENTS.OVERVIEW_ITEM_CTA]: {
    name: COMPONENTS.OVERVIEW_ITEM_CTA,
    component: flow([
      withOverviewCtaDataAdapter,
      withComponentLoader({
        skeleton: OverviewCtaSkeleton,
        checkDataLoaded: checkLoadedData(SUPPORTED_COMPONENTS.cta),
      }),
      dataProviders.overviewCtaDataProvider,
      withLocationUpdate,
      withModelFiltersUpdate({ shouldUpdateModelOnInit: false }),
    ])(OverviewCta),
    creativeId: LEAD_CTA_COMPONENT,
  },
  [COMPONENTS.OVERVIEW_ITEM_EXTRACT]: {
    name: COMPONENTS.OVERVIEW_ITEM_EXTRACT,
    component: flow(withComponentLoader({ skeleton: ExtractSkeleton }))(Extract),
  },
  [COMPONENTS.INVENTORY]: {
    name: COMPONENTS.INVENTORY,
    component: flow(
      withLocationUpdate,
      withInitialInventoryParamsUrlUpdate,
      withModelFiltersUpdate({}),
      withInventoryDataAdapter,
      withComponentLoader({
        skeleton: InventorySkeleton,
        checkDataLoaded: checkLoadedData(SUPPORTED_COMPONENTS.inventory),
      }),
      dataProviders.inventoryDataProvider
    )(Inventory),
    creativeId: INVENTORY_COMPONENT,
  },
  [COMPONENTS.VIN]: {
    name: COMPONENTS.VIN,
    component: flow(dataProviders.vinDataProvider, withComponentLoader({ skeleton: StubComponent }))(Vin),
  },
  [COMPONENTS.DEALERSHIP]: {
    name: COMPONENTS.DEALERSHIP,
    component: flow(
      withLocationUpdate,
      withModelFiltersUpdate({ shouldUpdateModelOnInit: false }),
      withComponentLoader({ skeleton: StubComponent }),
      withInitialFilters
    )(Dealership),
  },
  [COMPONENTS.DEALERSHIP_ITEM]: {
    name: COMPONENTS.DEALERSHIP_ITEM,
    component: flow([
      withComponentLoader({ skeleton: DealershipItemSkeleton }),
      withInitialFilters,
      dataProviders.dealershipItemDataProvider,
    ])(DealershipItem),
  },
  [COMPONENTS.LINKS]: {
    name: COMPONENTS.LINKS,
    component: withComponentLoader({ skeleton: LinksSkeleton })(Links),
    creativeId: LINKS_COMPONENT_CREATIVE_ID,
  },
  [COMPONENTS.LINKS_ITEM]: {
    name: COMPONENTS.LINKS_ITEM,
    component: withComponentLoader({ skeleton: StubComponent })(LinksItem),
  },
  [COMPONENTS.OVERVIEW_ITEM_LEARN_MORE]: {
    name: COMPONENTS.OVERVIEW_ITEM_LEARN_MORE,
    component: flow([withComponentLoader({ skeleton: LearnMoreSkeleton }), dataProviders.learnMoreDataProvider])(
      LearnMore
    ),
    creativeId: LEARN_MORE_COMPONENT_CREATIVE_ID,
  },
  [COMPONENTS.OVERVIEW_ITEM_VERDICT]: {
    name: COMPONENTS.OVERVIEW_ITEM_VERDICT,
    component: flow(withComponentLoader({ skeleton: VerdictSkeleton }), dataProviders.verdictDataProvider)(Verdict),
    creativeId: VERDICT_COMPONENT_CREATIVE_ID,
  },
  [COMPONENTS.ANSWER]: {
    name: COMPONENTS.ANSWER,
    component: flow(withComponentLoader({ skeleton: AnswerSkeleton }))(Answer),
  },
};
